import { useState } from 'react';
import { MAP_TYPES } from '../lib/sampleDataGenerator.js';
import { CITIES_BY_STATE, STATE_ORDER, STATE_META, findCityBySlug } from '../lib/cityPresets.js';

/**
 * Ali — build-map command panel for the Create page.
 *
 * This is the interactive counterpart to the Q&A `AliPanel` on the Result
 * page. Instead of static answers, Ali here exposes *commands* that mutate
 * the Create form directly. Each command is a scoped action (switch map
 * type, jump to a city, tighten/widen radius, seed the org+title from a
 * template) so users unfamiliar with the form can build a plausible demo
 * in a few clicks.
 *
 * All commands are local — no LLM round-trip. Production MAPTRIUM's Ali
 * uses Claude Sonnet 4.6 tool-use for open-ended requests; this demo
 * variant keeps the interaction free + instant + offline-friendly.
 */

// Command groups per map type: title + description templates keyed off city.
const PROJECT_TEMPLATES = {
  crash_safety: (city) => ({
    title: `${city?.name || 'Corridor'} High-Crash Corridor Analysis`,
    notes: `Three-year crash history for ${city?.name || 'the study area'} identifying hotspot clusters, severity breakdown, and candidate corridors for SS4A supplemental planning grant support.`,
  }),
  event_ops: (city) => ({
    title: `${city?.name || 'Downtown'} Public Event Operations Plan`,
    notes: `Ingress/egress and safety plan for a permitted mid-scale event in ${city?.name || 'downtown'} — gate positions, medical staging, security posts, vendor zones. Draft for pre-permit meeting.`,
  }),
  permit_row: (city) => ({
    title: `${city?.name || 'Regional'} Utility Corridor Permitting Study`,
    notes: `Right-of-way alignment for a utility corridor across ${city?.name || 'the study area'} identifying affected parcels, permit stack, and utility easement conflicts for pre-outreach.`,
  }),
  emergency_response: (city) => ({
    title: `${city?.name || 'County'} Emergency Response Readiness Map`,
    notes: `EOC-anchored response asset inventory for ${city?.name || 'the study area'} — fire/EMS coverage, hospital trauma level, shelter capacity, and active hazard footprint (${city?.state === 'CA' ? 'wildfire' : 'flood'}) planning scenario.`,
  }),
};

const COMMAND_GROUPS = [
  { key: 'setup',  label: 'Setup',   emoji: '🎯' },
  { key: 'city',   label: 'Location', emoji: '📍' },
  { key: 'scope',  label: 'Scope',   emoji: '🔍' },
  { key: 'copy',   label: 'Draft copy', emoji: '✏️' },
];

export default function AliCommands({
  mapType, setMapType,
  stateCode, setStateCode,
  citySlug, setCitySlug,
  setLat, setLon, setLocLabel,
  radiusScale, setRadiusScale,
  setOrg, setTitle, setNotes,
  selectedCity,
}) {
  const [open, setOpen] = useState(true);
  const [group, setGroup] = useState('setup');

  function pickCity(slug) {
    const c = findCityBySlug(slug);
    if (!c) return;
    setStateCode(c.state);
    setCitySlug(slug);
    setLat(String(c.lat));
    setLon(String(c.lon));
    setLocLabel(`${c.name}, ${c.state}`);
  }

  function draftFromTemplate() {
    const tpl = PROJECT_TEMPLATES[mapType]?.(selectedCity);
    if (!tpl) return;
    setTitle(tpl.title);
    setNotes(tpl.notes);
    if (selectedCity) {
      setOrg(`${selectedCity.name} — Public Works`);
    }
  }

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-atlas-paper transition-colors"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold"
            style={{ background: 'linear-gradient(135deg,#D4A437 0%,#8b6a1a 100%)' }}
          >
            A
          </span>
          <span className="text-sm font-semibold text-atlas-ink">Ask Ali to build it</span>
          <span className="text-xs text-atlas-muted">— MAPTRIUM assistant</span>
        </span>
        <span aria-hidden="true" className="text-atlas-muted text-lg leading-none">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-atlas-line">
          {/* Group tabs */}
          <div className="flex flex-wrap gap-1 my-3">
            {COMMAND_GROUPS.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => setGroup(g.key)}
                aria-pressed={group === g.key}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  group === g.key
                    ? 'bg-atlas-navy text-white border-atlas-navy'
                    : 'bg-white text-atlas-ink border-atlas-line hover:border-atlas-navy'
                }`}
              >
                <span aria-hidden="true" className="mr-1">{g.emoji}</span>{g.label}
              </button>
            ))}
          </div>

          {group === 'setup' && (
            <div className="space-y-2">
              <p className="text-xs text-atlas-muted">Switch map type:</p>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(MAP_TYPES).map(([key, t]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setMapType(key)}
                    className={`text-left text-xs px-2 py-1.5 rounded border transition-colors ${
                      mapType === key
                        ? 'border-atlas-gold bg-atlas-gold/10'
                        : 'border-atlas-line hover:bg-atlas-paper-2'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-atlas-muted mt-2">
                Each map type has different assets. Crash safety is best for grant apps; emergency response is best for EOC drills.
              </p>
            </div>
          )}

          {group === 'city' && (
            <div className="space-y-2">
              <p className="text-xs text-atlas-muted">Jump to a preset city (guaranteed on-land):</p>
              <div className="flex flex-wrap gap-1">
                {STATE_ORDER.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStateCode(s)}
                    aria-pressed={stateCode === s}
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded border transition-colors ${
                      stateCode === s ? 'text-white border-transparent' : 'bg-white border-atlas-line'
                    }`}
                    style={stateCode === s ? { backgroundColor: STATE_META[s].accent } : {}}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-1">
                {(CITIES_BY_STATE[stateCode] || []).map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => pickCity(c.slug)}
                    className={`text-left text-xs px-2 py-1 rounded border transition-colors ${
                      citySlug === c.slug
                        ? 'border-atlas-gold bg-atlas-gold/10 font-medium'
                        : 'border-atlas-line hover:bg-atlas-paper-2'
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {group === 'scope' && (
            <div className="space-y-2">
              <p className="text-xs text-atlas-muted">Set analysis scope:</p>
              <div className="grid grid-cols-3 gap-1.5">
                <button
                  type="button"
                  onClick={() => setRadiusScale(0.5)}
                  aria-pressed={Math.abs(radiusScale - 0.5) < 0.01}
                  className="text-xs px-2 py-1.5 rounded border border-atlas-line hover:bg-atlas-paper-2"
                >
                  Tight (½×)
                </button>
                <button
                  type="button"
                  onClick={() => setRadiusScale(1)}
                  aria-pressed={Math.abs(radiusScale - 1) < 0.01}
                  className="text-xs px-2 py-1.5 rounded border border-atlas-line hover:bg-atlas-paper-2"
                >
                  Normal (1×)
                </button>
                <button
                  type="button"
                  onClick={() => setRadiusScale(2)}
                  aria-pressed={Math.abs(radiusScale - 2) < 0.01}
                  className="text-xs px-2 py-1.5 rounded border border-atlas-line hover:bg-atlas-paper-2"
                >
                  Wide (2×)
                </button>
              </div>
              <p className="text-[11px] text-atlas-muted mt-2">
                Current scale {radiusScale.toFixed(2)}×. Wide is best for regional narratives; tight for corridor-specific studies.
              </p>
            </div>
          )}

          {group === 'copy' && (
            <div className="space-y-2">
              <p className="text-xs text-atlas-muted">
                Draft the org, title, and description for you based on the selected city + map type:
              </p>
              <button
                type="button"
                onClick={draftFromTemplate}
                className="btn-primary text-xs w-full"
              >
                Draft copy from template
              </button>
              <p className="text-[11px] text-atlas-muted mt-2">
                Overwrites the current values. In production MAPTRIUM, Ali writes the full narrative and cites the source data.
              </p>
            </div>
          )}

          <p className="mt-4 text-[11px] text-atlas-muted border-t border-atlas-line pt-3">
            Static command surface for the demo. In MAPTRIUM, Ali chains commands and drafts full grant narratives with source citations.
          </p>
        </div>
      )}
    </div>
  );
}
